import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useJornada } from '@/contexts/JornadaContext';
import { useTheme } from '@/contexts/ThemeContext';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import api from '@/lib/api';
import {
  ShoppingCart,
  Eye,
  Users,
  Building2,
  Package,
  MessageSquare,
  Bot,
  Headset,
  Clock,
  LogOut,
  TrendingUp,
  Settings,
  Image,
  Mail,
  Key,
  Building,
  ClipboardList,
  DollarSign,
  Bell,
  LayoutDashboard,
  Check,
  X,
  FileX,
  Sun,
  Moon,
  Monitor,
  Trophy,
  Menu,
  ExternalLink,
  Target,
  ChevronDown,
  ChevronRight,
  PanelLeftClose,
  PanelLeftOpen,
  Archive,
  Smartphone,
  FileSpreadsheet,
  PhoneOff,
  Database,
  BookOpen,
  SearchCheck,
  ImagePlus,
  ArrowRightLeft,
  MapPin
} from 'lucide-react';
import BannerCRM from '@/components/BannerCRM';
import AniversarioCelebration from '@/components/AniversarioCelebration';
import HoraExtraApproval from '@/components/HoraExtraApproval';
import UserAvatar from '@/components/UserAvatar';
import JornadaHeaderBar from '@/components/JornadaHeaderBar';
import NaoPerturbeAlerta from '@/components/NaoPerturbeAlerta';

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const { handleLogoutWithJornada } = useJornada();
  const { theme, setTheme, loadUserTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [notificacoes, setNotificacoes] = useState([]);
  const [naoLidas, setNaoLidas] = useState(0);
  const [showNotif, setShowNotif] = useState(false);
  const [supervisorEquipes, setSupervisorEquipes] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try { return localStorage.getItem('sidebar_collapsed') === 'true'; } catch { return false; }
  });
  const [collapsedSections, setCollapsedSections] = useState({});

  const toggleSidebar = () => {
    setSidebarCollapsed(prev => {
      const next = !prev;
      try { localStorage.setItem('sidebar_collapsed', String(next)); } catch {}
      return next;
    });
  };

  const toggleSection = (label) => {
    setCollapsedSections(prev => ({ ...prev, [label]: !prev[label] }));
  };

  useEffect(() => { loadUserTheme(); }, []);

  // Carregar equipes do supervisor para filtrar sidebar
  useEffect(() => {
    if (user?.perfil === 'SUPERVISOR') {
      api.get('/equipes').then(res => {
        const minhas = (res.data || []).filter(e => e.supervisor_id === user.id);
        setSupervisorEquipes(minhas);
      }).catch(() => {});
    }
  }, [user]);

  const loadNotificacoes = useCallback(async () => {
    if (document.hidden) return; // Não fazer request se a aba não está visível
    try {
      const [notifRes, countRes] = await Promise.all([
        api.get('/notificacoes'),
        api.get('/notificacoes/nao-lidas')
      ]);
      setNotificacoes(notifRes.data);
      const newCount = countRes.data.count;
      setNaoLidas(prev => {
        if (newCount > prev && prev >= 0) {
          try {
            const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1jZWVnamNjYV5cc3l8gIF9d3N4fn+BgoN/fnx6d3l7gH5/fXx+');
            audio.volume = 0.3;
            audio.play().catch(() => {});
          } catch {}
        }
        return newCount;
      });
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => {
    loadNotificacoes();
    const interval = setInterval(loadNotificacoes, 20000);
    // Recarregar imediatamente quando aba volta a ficar visível
    const onVisibility = () => { if (!document.hidden) loadNotificacoes(); };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [loadNotificacoes]);

  const marcarLida = async (id) => {
    await api.put(`/notificacoes/${id}/ler`);
    loadNotificacoes();
  };

  const marcarTodasLidas = async () => {
    await api.put('/notificacoes/ler-todas');
    loadNotificacoes();
  };

  const [saindo, setSaindo] = useState(false);

  const handleLogout = async () => {
    if (saindo) return;
    setSaindo(true);
    try {
      if (handleLogoutWithJornada) {
        await handleLogoutWithJornada();
      } else {
        logout();
      }
    } finally {
      setSaindo(false);
      navigate('/login');
    }
  };

  // Menu baseado no perfil - organizado por seções
  const getMenuSections = () => {
    const sections = [
      {
        label: 'PRINCIPAL',
        items: [
          { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, perfis: ['ADMIN', 'BKO', 'BKO_MAILING', 'VENDEDOR', 'GERENTE', 'SUPERVISOR'] },
          { path: '/vendas/nova', label: 'Nova Venda', icon: ShoppingCart, perfis: ['ADMIN', 'BKO', 'BKO_MAILING', 'VENDEDOR', 'GERENTE', 'SUPERVISOR'] },
          { path: '/vendas', label: 'Acompanhamento', icon: Eye, perfis: ['ADMIN', 'BKO', 'BKO_MAILING', 'VENDEDOR', 'GERENTE', 'SUPERVISOR'] },
          { path: '/movel', label: 'Móvel', icon: Smartphone, perfis: ['ADMIN', 'BKO', 'BKO_MAILING', 'VENDEDOR', 'GERENTE', 'SUPERVISOR'] },
          { path: '/viabilidade', label: 'Viabilidade Claro', icon: MapPin, perfis: ['ADMIN', 'BKO', 'BKO_MAILING', 'VENDEDOR', 'GERENTE', 'SUPERVISOR'] },
          { path: '/chat', label: 'Chat', icon: MessageSquare, perfis: ['ADMIN', 'BKO', 'BKO_MAILING', 'VENDEDOR', 'GERENTE', 'SUPERVISOR'] },
          { path: 'https://cnpja.com/person', label: 'Consulta CNPJ', icon: SearchCheck, externo: true, perfis: ['ADMIN', 'BKO', 'BKO_MAILING', 'VENDEDOR', 'GERENTE', 'SUPERVISOR'] },
        ]
      },
      {
        label: 'OPERACAO',
        items: [
          { path: '/mailing', label: 'Mailing', icon: Mail, perfis: ['ADMIN', 'BKO_MAILING', 'SUPERVISOR', 'VENDEDOR', 'GERENTE'] },
          { path: '/cancelamentos', label: 'Cancelamentos', icon: FileX, perfis: ['ADMIN', 'BKO', 'GERENTE'] },
          { path: '/chat-ia', label: 'Assistente IA', icon: Bot, perfis: ['ADMIN', 'BKO', 'BKO_MAILING', 'VENDEDOR', 'GERENTE', 'SUPERVISOR'] },
          { path: '/suporte', label: 'Suporte', icon: Headset, perfis: ['ADMIN', 'BKO', 'BKO_MAILING', 'VENDEDOR', 'GERENTE', 'SUPERVISOR'] },
          { path: '/material-apoio', label: 'Material de Apoio', icon: BookOpen, perfis: ['ADMIN', 'BKO', 'BKO_MAILING', 'VENDEDOR', 'GERENTE', 'SUPERVISOR'] },
          { path: '/backoffice-claro', label: 'Backoffice Claro', icon: ExternalLink, perfis: ['ADMIN'] },
          { path: '/metas', label: 'Metas de Vendas', icon: Target, perfis: ['ADMIN', 'BKO', 'BKO_MAILING', 'VENDEDOR', 'GERENTE', 'SUPERVISOR'] },
        ]
      },
      {
        label: 'GESTAO',
        items: [
          { path: '/painel/usuarios', label: 'Usuarios', icon: Users, perfis: ['ADMIN'] },
          { path: '/painel/empresas', label: 'Empresas', icon: Building2, perfis: ['ADMIN'] },
          { path: '/painel/sedes', label: 'Sedes', icon: Building, perfis: ['ADMIN'] },
          { path: '/painel/valores', label: 'Valores', icon: DollarSign, perfis: ['ADMIN'] },
          { path: '/painel/config-vendas', label: 'Config. Vendas', icon: Settings, perfis: ['ADMIN'] },
          { path: '/jornada-controle', label: 'Controle Jornada', icon: ClipboardList, perfis: ['ADMIN', 'GERENTE'] },
          { path: '/remanejar-claro', label: 'Remanejar Claro', icon: ArrowRightLeft, perfis: ['ADMIN', 'GERENTE'] },
        ]
      },
      {
        label: 'RESULTADOS',
        items: [
          { path: '/data-center', label: 'Data Center', icon: Database, perfis: ['ADMIN'] },
          { path: '/ranking-presencial', label: 'Ranking Presencial', icon: Trophy, perfis: ['ADMIN', 'GERENTE', 'SUPERVISOR'] },
          { path: '/ranking-homeoffice', label: 'Ranking Home Office', icon: Trophy, perfis: ['ADMIN', 'GERENTE', 'SUPERVISOR'] },
        ]
      },
      {
        label: 'SISTEMA',
        items: [
          { path: '/configuracoes/banner', label: 'Banner do CRM', icon: Image, perfis: ['ADMIN'] },
          { path: '/configuracoes/imagem-login', label: 'Imagem de Login', icon: ImagePlus, perfis: ['ADMIN'] },
          { path: '/seguranca', label: 'Seguranca', icon: Key, perfis: ['ADMIN'] },
          { path: '/google-sheets', label: 'Google Sheets', icon: FileSpreadsheet, perfis: ['ADMIN'] },
          { path: '/nao-perturbe', label: 'Lei Não Perturbe', icon: PhoneOff, perfis: ['ADMIN'] },
          { path: '/backups', label: 'Backup de Vendas', icon: Archive, perfis: ['ADMIN'] },
        ]
      },
    ];

    return sections.map(section => ({
      ...section,
      items: section.items.filter(item => {
        if (!item.perfis || !item.perfis.includes(user?.perfil)) return false;
        if (user?.perfil === 'SUPERVISOR') {
          if (item.path === '/ranking-presencial' || item.path === '/ranking-homeoffice') {
            return supervisorEquipes.length > 0;
          }
        }
        return true;
      })
    })).filter(section => section.items.length > 0);
  };

  const menuSections = getMenuSections();

  // Fechar sidebar mobile ao navegar (apenas mobile, não desktop)
  useEffect(() => {
    if (window.innerWidth < 1024) {
      setSidebarOpen(false);
    }
  }, [location.pathname]);

  return (
    <div className="flex h-screen bg-gray-50 dark:bg-[#0a0014] transition-colors">
      {/* Overlay mobile */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar Fixo - colapsável no desktop, overlay em mobile/tablet */}
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-50
        ${sidebarCollapsed ? 'w-[68px] min-w-[68px]' : 'w-[250px] min-w-[250px]'} bg-[#0f0520] text-white flex flex-col
        transform transition-all duration-200 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        {/* Header/Logo */}
        <div className={`p-5 border-b border-purple-900/60 flex items-center ${sidebarCollapsed ? 'justify-center' : 'justify-between'}`}>
          {sidebarCollapsed ? (
            <button onClick={toggleSidebar} className="w-9 h-9 bg-gradient-to-br from-red-500 via-purple-500 to-purple-600 rounded-lg flex items-center justify-center hover:brightness-110 transition-all shadow-lg shadow-purple-500/20" title="Expandir menu" data-testid="sidebar-expand-btn">
              <TrendingUp className="w-5 h-5 text-white" />
            </button>
          ) : (
            <>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-gradient-to-br from-red-500 via-purple-500 to-purple-600 rounded-lg flex items-center justify-center shadow-lg shadow-purple-500/20">
                  <TrendingUp className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className="text-lg font-bold tracking-tight leading-tight bg-gradient-to-r from-white to-purple-200 bg-clip-text text-transparent">MULTI TELECOM</h1>
                  <p className="text-[10px] text-purple-300/60">CRM System</p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={toggleSidebar} className="hidden lg:block text-purple-400/50 hover:text-purple-300 transition-colors" title="Recolher menu" data-testid="sidebar-collapse-btn">
                  <PanelLeftClose size={18} />
                </button>
                <button onClick={() => setSidebarOpen(false)} className="lg:hidden text-purple-400/50 hover:text-white">
                  <X size={20} />
                </button>
              </div>
            </>
          )}
        </div>

        {/* Menu Navigation */}
        <nav className="flex-1 p-3 overflow-y-auto">
          {menuSections.map((section, sIdx) => {
            const isCollapsed = collapsedSections[section.label];
            const hasActive = section.items.some(item => location.pathname === item.path);
            return (
              <div key={section.label} className={sIdx > 0 ? 'mt-3' : ''}>
                {!sidebarCollapsed && (
                  <button
                    onClick={() => toggleSection(section.label)}
                    className="w-full flex items-center justify-between px-3 py-1.5 group cursor-pointer"
                    data-testid={`section-toggle-${section.label.toLowerCase()}`}
                  >
                    <span className={`text-[10px] font-bold tracking-widest uppercase ${hasActive && isCollapsed ? 'text-purple-400' : 'text-purple-400/50 group-hover:text-purple-300'}`}>
                      {section.label}
                    </span>
                    {isCollapsed
                      ? <ChevronRight size={14} className="text-purple-400/50 group-hover:text-purple-300" />
                      : <ChevronDown size={14} className="text-purple-400/50 group-hover:text-purple-300" />
                    }
                  </button>
                )}
                {(sidebarCollapsed || !isCollapsed) && (
                  <div className={`space-y-0.5 ${sidebarCollapsed ? '' : 'mt-0.5'}`}>
                    {section.items.map((item) => {
                      const Icon = item.icon;
                      const isActive = location.pathname === item.path;

                      if (item.externo) {
                        return (
                          <a
                            key={item.path}
                            href={item.path}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`flex items-center ${sidebarCollapsed ? 'justify-center' : 'gap-3'} px-3 py-2 rounded-lg transition-all text-sm text-purple-200/70 hover:bg-purple-800/30 hover:text-purple-200`}
                            data-testid={`menu-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                            title={sidebarCollapsed ? item.label : undefined}
                          >
                            <Icon size={17} />
                            {!sidebarCollapsed && <span className="font-medium text-[13px]">{item.label}</span>}
                          </a>
                        );
                      }

                      return (
                        <Link
                          key={item.path}
                          to={item.path}
                          className={`flex items-center ${sidebarCollapsed ? 'justify-center' : 'gap-3'} px-3 py-2 rounded-lg transition-all text-sm ${
                            isActive
                              ? 'bg-gradient-to-r from-red-600 via-purple-600 to-purple-600 text-white shadow-lg shadow-purple-500/20'
                              : 'text-purple-200/70 hover:bg-purple-800/30 hover:text-purple-200'
                          }`}
                          data-testid={`menu-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                          title={sidebarCollapsed ? item.label : undefined}
                        >
                          <Icon size={17} />
                          {!sidebarCollapsed && <span className="font-medium text-[13px]">{item.label}</span>}
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* User Info & Logout */}
        <div className="p-3 border-t border-purple-900/60">
          {sidebarCollapsed ? (
            <div className="flex flex-col items-center gap-2">
              <UserAvatar fotoPath={user?.foto_path} nome={user?.nome} size="md" />
              <button
                onClick={handleLogout}
                disabled={saindo}
                className="w-full flex items-center justify-center p-2 border border-purple-700/50 rounded-lg text-purple-200/70 hover:bg-red-600/20 hover:border-purple-500/50 transition-colors disabled:opacity-60"
                data-testid="logout-button"
                title="Sair"
              >
                <LogOut size={15} />
              </button>
            </div>
          ) : (
            <>
              <div className="mb-2 flex items-center gap-2">
                <UserAvatar fotoPath={user?.foto_path} nome={user?.nome} size="md" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-white truncate">{user?.nome}</p>
                  <p className="text-xs text-purple-300/50 truncate">{user?.email}</p>
                  <span className="inline-block mt-1 px-2 py-0.5 bg-gradient-to-r from-red-600 to-purple-600 text-white text-[10px] font-bold rounded shadow-sm">
                    {user?.perfil}
                  </span>
                </div>
              </div>
              <button
                onClick={handleLogout}
                disabled={saindo}
                className="w-full flex items-center justify-center gap-2 px-3 py-2 border border-purple-700/50 rounded-lg text-purple-200/70 hover:bg-red-600/20 hover:border-purple-500/50 transition-colors text-sm disabled:opacity-60"
                data-testid="logout-button"
              >
                <LogOut size={15} />
                <span>{saindo ? 'Registrando saída...' : 'Sair'}</span>
              </button>
            </>
          )}
        </div>
      </aside>

      {/* Conteúdo Principal - ocupa o restante */}
      <main className="flex-1 overflow-auto flex flex-col relative bg-gray-50 dark:bg-[#0a0014] transition-colors min-w-0">
        {/* Barra Superior */}
        <div className="flex items-center justify-between gap-2 px-4 lg:px-6 py-2 bg-white dark:bg-[#120525] border-b border-gray-200 dark:border-purple-900/40 relative sticky top-0 z-30">
          {/* Botão hambúrguer mobile */}
          <button
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-purple-900/40"
            data-testid="btn-menu-mobile"
          >
            <Menu size={22} className="text-gray-600 dark:text-gray-300" />
          </button>

          {/* Botão expandir sidebar desktop */}
          {sidebarCollapsed && (
            <button
              onClick={toggleSidebar}
              className="hidden lg:flex p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-purple-900/40 transition-colors"
              data-testid="btn-expand-sidebar"
              title="Expandir menu"
            >
              <PanelLeftOpen size={20} className="text-gray-600 dark:text-gray-300" />
            </button>
          )}

          <div className="flex-1" />

          <div className="flex items-center gap-2">
          <JornadaHeaderBar onSair={handleLogout} saindo={saindo} />
          {/* Theme Toggle */}
          <div className="flex items-center bg-gray-100 dark:bg-purple-900/30 rounded-lg p-1 gap-0.5" data-testid="theme-toggle">
            <button
              onClick={() => setTheme('light')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${theme === 'light' ? 'bg-white dark:bg-purple-900/20 shadow-sm text-amber-600' : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'}`}
              title="Modo Claro"
              data-testid="theme-light"
            >
              <Sun className="w-4 h-4" />
              <span className="hidden sm:inline">Claro</span>
            </button>
            <button
              onClick={() => setTheme('system')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${theme === 'system' ? 'bg-white dark:bg-purple-900/20 shadow-sm text-purple-500' : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'}`}
              title="Sistema"
              data-testid="theme-system"
            >
              <Monitor className="w-4 h-4" />
              <span className="hidden sm:inline">Auto</span>
            </button>
            <button
              onClick={() => setTheme('dark')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${theme === 'dark' ? 'bg-white dark:bg-purple-900/20 shadow-sm text-gray-700' : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'}`}
              title="Modo Escuro"
              data-testid="theme-dark"
            >
              <Moon className="w-4 h-4" />
              <span className="hidden sm:inline">Escuro</span>
            </button>
          </div>

          <button
            onClick={() => setShowNotif(!showNotif)}
            className="relative p-2 rounded-full hover:bg-gray-100 dark:hover:bg-purple-900/40 transition-colors"
            data-testid="btn-notificacoes"
          >
            <Bell className={`w-5 h-5 ${naoLidas > 0 ? 'text-red-500 animate-bounce' : 'text-gray-600 dark:text-gray-300'}`} />
            {naoLidas > 0 && (
              <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-xs font-bold w-5 h-5 flex items-center justify-center rounded-full animate-pulse">
                {naoLidas > 9 ? '9+' : naoLidas}
              </span>
            )}
          </button>

          {/* Dropdown Notificações */}
          {showNotif && (
            <div className="absolute top-12 right-4 w-96 bg-white dark:bg-[#120525] border border-gray-200 dark:border-purple-900/40 rounded-xl shadow-2xl z-50 max-h-96 overflow-hidden" data-testid="notif-dropdown">
              <div className="flex items-center justify-between p-3 border-b bg-gray-50 dark:bg-purple-900/30/50">
                <span className="font-semibold text-sm text-gray-700 dark:text-gray-200">Notificacoes {naoLidas > 0 && <span className="text-red-500">({naoLidas})</span>}</span>
                <div className="flex gap-2">
                  {naoLidas > 0 && (
                    <button onClick={marcarTodasLidas} className="text-xs text-purple-500 hover:underline" data-testid="btn-ler-todas">
                      Marcar todas como lidas
                    </button>
                  )}
                  <button onClick={() => setShowNotif(false)} className="text-gray-400 hover:text-gray-600">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <div className="overflow-y-auto max-h-80">
                {notificacoes.length === 0 ? (
                  <p className="text-center text-gray-400 text-sm py-8">Nenhuma notificacao</p>
                ) : (
                  notificacoes.map(n => (
                    <div
                      key={n.id}
                      className={`p-3 border-b hover:bg-gray-50 dark:hover:bg-purple-900/40/50 cursor-pointer transition-colors ${!n.lida ? 'bg-purple-50 dark:bg-purple-900/20 border-l-4 border-l-purple-500' : ''}`}
                      onClick={() => { marcarLida(n.id); if (n.venda_id) navigate(`/vendas/${n.venda_id}`); else if (n.tipo === 'material_apoio') navigate('/material-apoio'); setShowNotif(false); }}
                    >
                      <div className="flex items-start gap-2">
                        <div className={`mt-0.5 w-2 h-2 rounded-full flex-shrink-0 ${!n.lida ? 'bg-purple-500' : 'bg-gray-300'}`} />
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm ${!n.lida ? 'font-semibold text-gray-900 dark:text-white' : 'text-gray-600 dark:text-gray-400'}`}>{n.titulo}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">{n.mensagem}</p>
                          <p className="text-xs text-gray-400 mt-1">{n.criado_em?.slice(0,10).split('-').reverse().join('/')} {n.criado_em?.slice(11,16)} - {n.criado_por}</p>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
          </div>
        </div>

        {/* Alerta Lei Não Perturbe (token expirado) */}
        <NaoPerturbeAlerta />

        {/* Banner do CRM */}
        <BannerCRM />

        {/* Celebração de Aniversário */}
        <AniversarioCelebration />

        {/* Aprovação de Hora Extra (Admin) */}
        <HoraExtraApproval />
        
        <div className="flex-1 p-4 lg:p-8">
          {children}
        </div>
        
        {/* Marca Multi Telecom */}
        <div className="fixed bottom-2 right-3 text-[10px] text-gray-400 opacity-60 select-none pointer-events-none z-50">
          Multi Telecom
        </div>
      </main>
    </div>
  );
};

export default Layout;
